import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type FormRenderProps } from '@saviynt/biz-entity-catalog';
import {
  Icon,
  InlineMessage,
  InputField,
  MenuInput,
  type MenuInputOption,
  RadioButton,
  Tooltip,
  Typography,
  Upload,
  useUploadFile,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/utils/localization';
import { ApiError } from '../data-sources';
import {
  type Client,
  type ClientFormData,
  type ClientFormSubmitPayload,
} from '../types';
import {
  extractApiFieldErrors,
  validateClientForm,
  validateJwksUrl,
} from '../utils';

import styles from './ClientForm.module.css';

type ClientFormProps = FormRenderProps<Client> & {
  /** Available role options fetched from the backend */
  allowedRoles?: string[];
};

/**
 * Create/Edit form for client credentials.
 * Rendered via BizEntityCatalog's renderForm prop.
 * Layout matches Figma: Client Name + Roles side-by-side,
 * card-styled radio buttons for key source, DS Upload dropzone for certificate.
 */
export function ClientForm({
  mode,
  data,
  onSubmit,
  // onCancel and isSubmitting are provided by FormRenderProps but handled
  // by BizEntityCatalogModal's FooterActionBar, so we omit them here.
  validationErrors: externalErrors = {},
  allowedRoles = [],
  getFormData,
  formRef,
}: ClientFormProps): JSX.Element {
  const l = useInvertedLocalize();
  const isEdit = mode === 'edit';

  const [clientName, setClientName] = useState(data?.clientName ?? '');
  const [description, setDescription] = useState(data?.description ?? '');
  const [selectedRoles, setSelectedRoles] = useState<string[]>(
    data?.roles ?? []
  );
  const [selectedRoleOptions, setSelectedRoleOptions] = useState<
    MenuInputOption[]
  >(() =>
    (data?.roles ?? []).map((role) => ({
      label: role,
      value: role,
      isChecked: true,
    }))
  );
  const [customClaims, setCustomClaims] = useState(
    data?.customClaims ? JSON.stringify(data.customClaims, null, 2) : ''
  );
  const [keySource, setKeySource] = useState<'certificate' | 'jwksUrl' | ''>(
    () => {
      if (!isEdit) {
        return 'certificate';
      }

      // Edit mode: always start unselected — user must explicitly choose to change key source
      return '';
    }
  );
  const [jwksUrl, setJwksUrl] = useState(data?.jwksUrl ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Sync form state when data prop changes (e.g. BEC fetches fresh data via useGetByIdHook
  // after the modal opens). Without this, useState keeps stale values from the previous edit.
  // We intentionally key on dataId (not individual fields) so the effect only fires when a
  // different client is loaded, not on every data reference change.
  const dataId = data?.clientId ?? data?.id;

  useEffect(() => {
    setClientName(data?.clientName ?? '');
    setDescription(data?.description ?? '');
    setSelectedRoles(data?.roles ?? []);
    setSelectedRoleOptions(
      (data?.roles ?? []).map((role) => ({
        label: role,
        value: role,
        isChecked: true,
      }))
    );
    setCustomClaims(
      data?.customClaims ? JSON.stringify(data.customClaims, null, 2) : ''
    );
    setKeySource(isEdit ? '' : 'certificate');
    setJwksUrl(data?.jwksUrl ?? '');
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Keyed on dataId; adding data fields would reset user edits on every re-render.
  }, [dataId, isEdit]);

  /** Validate JWKS URL on blur so the user gets immediate feedback */
  const handleJwksUrlBlur = (): void => {
    if (!jwksUrl.trim()) {
      // Don't show error for empty — submit validation handles "required"
      setFieldErrors((prev) => {
        const next = { ...prev };

        delete next.jwksUrl;

        return next;
      });

      return;
    }

    const error = validateJwksUrl(jwksUrl);

    if (error) {
      setFieldErrors((prev) => ({ ...prev, jwksUrl: error }));
    } else {
      setFieldErrors((prev) => {
        const next = { ...prev };

        delete next.jwksUrl;

        return next;
      });
    }
  };

  /** Clear JWKS URL error as user types */
  const handleJwksUrlChange = (value: string): void => {
    setJwksUrl(value);

    if (fieldErrors.jwksUrl) {
      setFieldErrors((prev) => {
        const next = { ...prev };

        delete next.jwksUrl;

        return next;
      });
    }
  };

  // DS Upload hook for certificate file handling
  const {
    uploadedFiles,
    handleFileSelect,
    handleFileDelete,
    isMaxNumberUploaded,
    isMaxTotalSizeReached,
    maxFileSize,
    maxTotalFileSize,
  } = useUploadFile();

  // Sync external validation errors (e.g. from API 400 responses) into local state
  useEffect(() => {
    if (Object.keys(externalErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...externalErrors }));
    }
  }, [externalErrors]);

  // Register form data getter so BizEntityCatalogModal can read current form state
  // for its isSaveValid check (enables the Save/Create button).
  // Use refs to avoid infinite re-render loop: getFormData is a new function on every
  // modal render, so including it as a useEffect dependency would cause a cycle.
  const getFormDataRef = useRef(getFormData);

  getFormDataRef.current = getFormData;

  const buildFormData = (): Partial<Client> | null => {
    // Parse customClaims JSON string to Record for the Client type
    let parsedClaims: Record<string, string> | undefined;

    if (customClaims.trim()) {
      try {
        parsedClaims = JSON.parse(customClaims) as Record<string, string>;
      } catch {
        // Invalid JSON — omit from form data
      }
    }

    return {
      // Include id so BizEntityCatalog can identify the item for updates
      ...(data?.clientId ? { id: data.clientId, clientId: data.clientId } : {}),
      clientName,
      description,
      roles: selectedRoles,
      customClaims: parsedClaims,
    };
  };

  // Register once on mount
  useEffect(() => {
    getFormDataRef.current?.(buildFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-register when form values change so isSaveValid re-evaluates
  useEffect(() => {
    getFormDataRef.current?.(buildFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName, description, selectedRoles, customClaims, data]);

  // Merge default role options with any existing roles from data (edit mode)
  const roleOptions: MenuInputOption[] = useMemo(() => {
    const allRoles = new Set([...allowedRoles, ...selectedRoles]);

    return Array.from(allRoles).map((role) => ({
      label: role,
      value: role,
      isChecked: selectedRoles.includes(role),
    }));
  }, [allowedRoles, selectedRoles]);

  const handleRolesChange = (
    _clickedOption: MenuInputOption,
    selections: MenuInputOption[] | undefined
  ): void => {
    const safeSelections = selections ?? [];

    setSelectedRoles(safeSelections.map((opt) => opt.value));
  };

  const handleKeySourceClick = (_e: React.MouseEvent, value: string): void => {
    setKeySource(value as 'certificate' | 'jwksUrl');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Extract the first uploaded file (if any) for the certificate
    const certFile = uploadedFiles.length > 0 ? uploadedFiles[0].file : null;

    const formData: Partial<ClientFormData> = {
      clientName,
      description,
      roles: selectedRoles,
      customClaims,
      // Only include key source fields when a selection has been made
      ...(keySource ? { keySource, publicCert: certFile, jwksUrl } : {}),
    };

    const errors = validateClientForm(formData);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);

      return;
    }

    setFieldErrors({});

    // Parse customClaims JSON string — safe because validateClientForm already validated it
    let parsedClaims: Record<string, string> | undefined;

    if (customClaims.trim()) {
      try {
        parsedClaims = JSON.parse(customClaims) as Record<string, string>;
      } catch {
        setFieldErrors({ customClaims: 'Custom claims must be valid JSON.' });

        return;
      }
    }

    // Read certificate file contents as text so we can send JSON (backend doesn't support multipart)
    let publicCertText: string | undefined;

    if (keySource === 'certificate' && certFile) {
      try {
        publicCertText = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsText(certFile);
        });
      } catch {
        setFieldErrors({
          publicCert: 'Failed to read certificate file contents.',
        });

        return;
      }
    }

    // Cast through ClientFormSubmitPayload → Partial<Client> because
    // BizEntityCatalog's FormRenderProps constrains onSubmit to Partial<Client>,
    // but the form includes extra fields (publicCert, jwksUrl) that useClientCreate
    // needs. useClientCreate casts back to ClientFormData on the receiving end.
    const payload: ClientFormSubmitPayload = {
      clientName,
      description,
      roles: selectedRoles,
      customClaims: parsedClaims,
      ...(keySource === 'certificate' ? { publicCert: publicCertText } : {}),
      ...(keySource === 'jwksUrl' ? { jwksUrl } : {}),
    };

    try {
      await onSubmit(payload as unknown as Partial<Client>);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const serverFieldErrors = extractApiFieldErrors(err.body);

        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
        }
      }
    }
  };

  const renderFieldError = (field: string): JSX.Element | null => {
    const msg = fieldErrors[field];

    if (!msg) {
      return null;
    }

    return (
      <InlineMessage
        text={msg}
        colorTheme='critical'
        size='small'
        leftIcon={<Icon kind='AlertCriticalFilled' />}
      />
    );
  };

  return (
    <form
      ref={formRef as React.RefObject<HTMLFormElement>}
      onSubmit={(e) => {
        handleSubmit(e).catch(() => {});
      }}
      noValidate
      className={styles.ClientForm}>
      {/* Client Name + Roles side-by-side per Figma */}
      <div className={styles.ClientForm_row}>
        <div className={styles.ClientForm_field}>
          <InputField
            name='clientName'
            placeholder={l('Enter client name')}
            value={clientName}
            setValue={setClientName}
            isRequired
            isDisabled={isEdit}
            isCritical={!!fieldErrors.clientName}
            label={
              <Typography kind='label' htmlFor='clientName'>
                {l('Client Name')}
              </Typography>
            }
            CriticalHelperText={renderFieldError('clientName')}
          />
        </div>
        <div className={styles.ClientForm_field}>
          <MenuInput
            label='ClientForm_roles'
            isMulti
            options={roleOptions}
            selectedOptions={selectedRoleOptions}
            setSelectedOptions={setSelectedRoleOptions}
            onChange={handleRolesChange}
            isCritical={!!fieldErrors.roles}
            isPortalEnabled={false}
            inputLabel={
              <Typography kind='label' htmlFor='ClientForm_roles'>
                {l('Roles')}
              </Typography>
            }
            placeholder={l('Select roles')}
          />
          {renderFieldError('roles')}
        </div>
      </div>

      <div>
        <InputField
          name='description'
          placeholder={l('Optional description')}
          value={description}
          setValue={setDescription}
          kind='multiline'
          rows={3}
          isCritical={!!fieldErrors.description}
          label={
            <Typography kind='label' htmlFor='description'>
              {l('Description')}
            </Typography>
          }
          CriticalHelperText={renderFieldError('description')}
        />
      </div>

      <div>
        <InputField
          name='customClaims'
          placeholder='{"key": "value"}'
          value={customClaims}
          setValue={setCustomClaims}
          kind='multiline'
          rows={4}
          isCritical={!!fieldErrors.customClaims}
          label={
            <Typography kind='label' htmlFor='customClaims'>
              {l('Custom Claims (JSON)')}
            </Typography>
          }
          CriticalHelperText={renderFieldError('customClaims')}
        />
      </div>

      {/* Card-styled radio buttons with descriptions per Figma */}
      <fieldset className={styles.ClientForm_fieldset}>
        <legend className={styles.ClientForm_legend}>
          <Typography kind='label'>{l('Key Source')}</Typography>
          <Tooltip
            text={l(
              'Choose how the client authenticates: upload a certificate file or provide a JWKS endpoint URL.'
            )}>
            <Icon
              kind='AlertInfoFilled'
              size='xxSmall'
              color='--color-foreground-neutral-subtle'
            />
          </Tooltip>
        </legend>
        <div className={styles.ClientForm_radioRow}>
          <RadioButton
            id='keySource-certificate'
            name='keySource'
            title={l('Public Certificate')}
            description={l(
              'A static, manually uploaded file containing a public key, requiring manual updates whenever the certificate expires or rotates.'
            )}
            value='certificate'
            radioSelection={keySource}
            onClick={handleKeySourceClick}
          />
          <RadioButton
            id='keySource-jwksUrl'
            name='keySource'
            title={l('JWKS URL')}
            description={l(
              'A web endpoint that allows your system to automatically fetch and rotate public keys without manual intervention.'
            )}
            value='jwksUrl'
            radioSelection={keySource}
            onClick={handleKeySourceClick}
          />
        </div>
        {renderFieldError('keySource')}
      </fieldset>

      {/* DS Upload dropzone for certificate per Figma */}
      {keySource === 'certificate' && (
        <div>
          <Typography kind='label'>
            {l('Public Certificate (.pem, .crt)')}
          </Typography>
          <Upload
            kind='dropzone'
            isMultiple={false}
            onFileSelect={handleFileSelect}
            onFileDelete={handleFileDelete}
            uploadedFiles={uploadedFiles}
            acceptTypes='.pem,.crt'
            isMaxNumberUploaded={isMaxNumberUploaded}
            isMaxTotalSizeReached={isMaxTotalSizeReached}
            maxFileSize={maxFileSize}
            maxTotalFileSize={maxTotalFileSize}
          />
          {renderFieldError('publicCert')}
        </div>
      )}

      {keySource === 'jwksUrl' && (
        <div>
          <InputField
            name='jwksUrl'
            placeholder='https://example.com/.well-known/jwks.json'
            value={jwksUrl}
            setValue={handleJwksUrlChange}
            onBlur={handleJwksUrlBlur}
            isRequired
            isCritical={!!fieldErrors.jwksUrl}
            label={
              <Typography kind='label' htmlFor='jwksUrl'>
                {l('JWKS URL')}
              </Typography>
            }
            CriticalHelperText={renderFieldError('jwksUrl')}
          />
        </div>
      )}

      {/* Footer buttons are provided by BizEntityCatalogModal's FooterActionBar */}
    </form>
  );
}
